import type {Driver} from 'driver.js';
import type {Container} from '../../container.ts';
import type {OnboardingApiSectionName, OnboardingDriveStep, OnboardingStepId} from './types.ts';

/**
 * Moves to the specified step when an element is clicked, if the driver is active.
 *
 * @param {number} step - The step to move to when the element is clicked.
 * @param {HTMLElement} el - The HTML element to attach the click event listener to.
 * @param {Driver} driverObj - An object representing the driver controlling the wizard.
 */
export function moveToStep(step: number, el: Element, driverObj: Driver) {
  function clickHandler() {
    if (!driverObj.isActive()) return;
    driverObj.drive(step);
  }
  // only add the event listener once to avoid multiple event listeners
  // and to avoid event firing when the wizard is not active
  el.addEventListener('click', clickHandler, {once: true});
}

/**
 * Completes a specific onboarding category by sending a POST request to the server.
 *
 * @param {string} categoryName - The name of the onboarding category to complete.
 * @param {string} token - The CSRF token required for authentication.
 * @param {string} currentIndex - The current index of the step in the onboarding category.
 * @param {boolean} skipped - Whether or not the onboarding category was skipped.
 * @param {Container} container - The dependency injection providing access to necessary services.
 * @returns {Promise<any>} A promise that resolves with the result of the completion process.
 * @throws {Error} Throws an error if there was a problem completing the onboarding.
 */
export async function completeOnboarding(
  categoryName: OnboardingApiSectionName,
  token: string,
  currentIndex: number,
  skipped: boolean = false,
  container: Container,
) {
  const appSubUrl = container.lookup('appSubUrl');
  const fetcher = container.lookup('fetcher');

  try {
    const response = await fetcher.fetch(
      `${getInstanceBaseUrl(appSubUrl)}/onboarding/toggle_category`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
        body: JSON.stringify({
          category_name: categoryName,
          steps_completed: currentIndex,
          skipped,
        }),
      },
    );

    if (!response.ok) {
      // Handle non-successful HTTP responses here
      throw new Error('There was a problem completing the onboarding.');
    }

    return response;
  } catch {
    throw new Error('There was a problem completing the onboarding.');
  }
}

/**
 * Get the index of a step with a specified ID from a driver configuration.
 *
 * @param {Driver} driverObj - An object representing the driver controlling the wizard.
 * @param {string} id - The ID of the step to search for.
 * @returns {number} - The index of the step with the specified ID, or -1 if not found.
 */
export function getStepById(driverObj: Driver, id: OnboardingStepId) {
  const config = driverObj.getConfig();
  const stepIdx = config.steps.findIndex((step) => (step as OnboardingDriveStep)?.id === id);
  return stepIdx;
}

/**
 * Removes a specified URL parameter from the current URL without reloading the page.
 * @param {string} parameter - The name of the parameter to remove.
 */
export function removeURLParameter(parameter: string) {
  const currentURL = window.location.href;
  const url = new URL(currentURL);
  url.searchParams.delete(parameter);
  // Update the browser's address bar without reloading the page
  window.history.replaceState({}, '', url.toString());
}

/**
 * Some AllSpice instances use a sub-URL, meaning the AllSpice application is served under a specific path
 * We need to account for this sub-URL when constructing the base URL. The 'appSubUrl' may start with a
 * slash if it's a sub-URL, so we need to remove it. Additionally, root URLs by default end with a slash,
 * so we need to remove it as well. This ensures that the base URL is correctly constructed without any
 * redundant slashes.
 * @param {string} appSubUrl - The sub-URL specific to the application.
 * @returns {string} - The base URL of the AllSpice instance.
*/
export function getInstanceBaseUrl(appSubUrl: string) {
  return window.location.origin + appSubUrl;
}
